import { ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { HandThumbsUp, HandThumbsUpFill, Chat, ChatFill } from 'react-bootstrap-icons'
import ProfileAvatar from './ProfileAvatar'
import { formatRelativeToPar } from '../../utils/scoringFormatters'
import styles from './FeedItemRow.module.css'

const DAY_KEYS = ['su', 'ma', 'ti', 'ke', 'to', 'pe', 'la']

export interface FeedItemScore {
  status: string
  totalScore?: number | null
  netTotalScore?: number | null
  toPar?: number | null
  holesCompleted?: number | null
  totalHoles?: number | null
  resultType?: string | null
}

export interface FeedItemSocial {
  reactionCount: number
  commentCount: number
  hasUserReacted: boolean
  hasUserCommented: boolean
}

interface FeedItemRowProps {
  date?: string | null
  title: string
  subtitle?: ReactNode
  extraTitleLine?: string | null
  score?: FeedItemScore | null
  social?: FeedItemSocial | null
  image?: ReactNode
  avatar?: { imageUrl?: string | null; name: string } | null
  live?: boolean
  badge?: ReactNode
  onClick?: () => void
}

const FeedItemRow = ({
  date,
  title,
  subtitle,
  extraTitleLine,
  score,
  social,
  image,
  avatar,
  live,
  badge,
  onClick
}: FeedItemRowProps) => {
  const { t } = useTranslation(['dashboard', 'common'])

  let dayName = ''
  let dateText = ''
  if (date) {
    const d = new Date(date)
    dayName = t('dashboard:manager.weather.days_short.' + DAY_KEYS[d.getDay()])
    dateText = `${d.getDate()}.${d.getMonth() + 1}.`
  }

  const renderScoreColumn = () => {
    if (!score) return null
    if (score.totalScore === null || score.totalScore === undefined) return null
    if (!score.holesCompleted) return null

    const scoreSuffix = score.resultType?.startsWith('STABLEFORD') ? 'p' : ''

    if (score.status === 'COMPLETED') {
      const isFullRound = score.holesCompleted === score.totalHoles
      return (
        <div className={styles.scoreColumn}>
          <div className={styles.scoreMain}>
            <span>{score.totalScore}{scoreSuffix}</span>
            {isFullRound && score.netTotalScore != null && (
              <span className={styles.scoreNet}> ({score.netTotalScore})</span>
            )}
          </div>
          <div className={styles.scoreSecondary}>F</div>
        </div>
      )
    }

    const toParStr = score.toPar == null ? '' : formatRelativeToPar(score.toPar).text

    return (
      <div className={styles.scoreColumn}>
        <div className={styles.scoreMain}>
          {toParStr && <span>{toParStr}</span>}
        </div>
        <div className={styles.scoreSecondary}>
          {score.totalHoles != null
            ? `${score.holesCompleted}/${score.totalHoles}`
            : score.holesCompleted}
        </div>
      </div>
    )
  }

  const renderImage = () => {
    if (image) return image
    if (!avatar) return null
    if (avatar.imageUrl) {
      return <ProfileAvatar profileImageUrl={avatar.imageUrl} userName={avatar.name} size={48} />
    }
    return (
      <div className={styles.avatarPlaceholder}>
        {avatar.name.split(' ').map(n => n.charAt(0)).join('').toUpperCase().slice(0, 2)}
      </div>
    )
  }

  const ReactionIcon = social?.hasUserReacted ? HandThumbsUpFill : HandThumbsUp
  const CommentIcon = social?.hasUserCommented ? ChatFill : Chat

  return (
    <div
      className={`${styles.item} ${onClick ? styles.itemClickable : ''}`}
      onClick={onClick}
      role={onClick ? 'button' : undefined}
      tabIndex={onClick ? 0 : undefined}
      onKeyDown={onClick ? (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault()
          onClick()
        }
      } : undefined}
    >
      <div className={styles.topRow}>
        <div className={styles.dateColumn}>
          <div className={styles.dayName}>{dayName}</div>
          <div className={styles.date}>{dateText}</div>
        </div>
        <div className={styles.contentColumn}>
          <div className={styles.title}>{title}</div>
          <div className={styles.subtitle}>{subtitle}</div>
          {extraTitleLine && <div className={styles.title}>{extraTitleLine}</div>}
          {social && (
            <div className={styles.social}>
              <button
                className={`${styles.socialBtn} ${social.hasUserReacted ? styles.socialActive : ''}`}
                type="button"
                aria-label={t('common:actions.like')}
              >
                <ReactionIcon size={16} />
                <span className={styles.socialCount}>{social.reactionCount}</span>
              </button>
              <button
                className={`${styles.socialBtn} ${social.hasUserCommented ? styles.socialActive : ''}`}
                type="button"
                aria-label={t('common:actions.comment')}
              >
                <CommentIcon size={16} />
                <span className={styles.socialCount}>{social.commentCount}</span>
              </button>
            </div>
          )}
        </div>
        {renderScoreColumn()}
        <div className={styles.imageColumn}>
          {renderImage()}
          {live && (
            <div className={styles.liveBadge}>
              <span className={styles.liveDot} />
              LIVE
            </div>
          )}
          {badge}
        </div>
      </div>
    </div>
  )
}

export default FeedItemRow
